import React, { useEffect, useState } from "react";
import { Search, ArrowLeft, Ban, CheckCircle2, ShieldAlert, ShieldOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import StatusBadge from "../StatusBadge";

const RISK_STYLE = {
  NORMAL: "text-emerald-400", REVIEW: "text-indigo-300", BLOCKED: "text-rose-400",
};

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [risk, setRisk] = useState("");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (risk) params.risk = risk;
      const { data } = await api.get("/admin/customers", { params });
      setRows(data.customers);
    } catch { toast.error("Could not load customers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, risk]);

  if (sel) return <CustomerDetail userId={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div className="space-y-5" data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold">Customers <span className="text-sm font-normal text-slate-500">({rows.length})</span></h1>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
            <Search className="h-4 w-4 text-slate-500" />
            <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / email / mobile" className="w-52 bg-transparent text-sm outline-none placeholder:text-slate-600" />
          </div>
          <select data-testid="customer-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
            <option value="">All statuses</option><option value="ACTIVE">Active</option><option value="SUSPENDED">Suspended</option>
          </select>
          <select data-testid="customer-filter-risk" value={risk} onChange={(e) => setRisk(e.target.value)} className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm">
            <option value="">All risk</option><option value="NORMAL">Normal</option><option value="REVIEW">Review</option><option value="BLOCKED">Blocked</option>
          </select>
        </div>
      </div>

      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Customer</th><th className="p-4 text-left">Mobile</th><th className="p-4 text-right">Available</th><th className="p-4 text-right">Pending</th><th className="p-4 text-right">Tokens</th><th className="p-4 text-left">Status</th><th className="p-4 text-left">Risk</th></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.user_id} data-testid={`customer-row-${c.user_id}`} onClick={() => setSel(c.user_id)} className="cursor-pointer border-t border-slate-800/60 hover:bg-white/5">
                  <td className="p-4"><p className="font-semibold">{c.name || "—"}</p><p className="font-mono text-xs text-slate-500">{c.email}</p></td>
                  <td className="p-4 text-slate-400">{c.mobile || "—"}</td>
                  <td className="p-4 text-right font-mono text-emerald-400">{c.available_display}</td>
                  <td className="p-4 text-right font-mono text-slate-400">{c.pending_display}</td>
                  <td className="p-4 text-right font-mono">{c.token_count}</td>
                  <td className="p-4"><StatusBadge status={c.status === "SUSPENDED" ? "BLOCKED" : "ACTIVE"} /></td>
                  <td className={`p-4 font-mono text-xs ${RISK_STYLE[c.risk_state] || "text-slate-400"}`}>{c.risk_state}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No customers found.</td></tr>}
              {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DETAIL_TABS = ["Overview", "Wallet", "Tokens", "Cashback", "Redemptions", "Fraud", "Audit"];

function CustomerDetail({ userId, onBack }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/customers/${userId}`).then((r) => setD(r.data)).catch(() => toast.error("Could not load customer"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const act = async (action, needReason) => {
    let reason = "";
    if (needReason) { reason = prompt(`Reason for ${action}?`) || ""; if (!reason) return; }
    setBusy(true);
    try { await api.post("/admin/customers/action", { user_id: userId, action, reason }); toast.success(`${action} done`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  const p = d.profile;
  const suspended = p.status === "SUSPENDED";

  return (
    <div className="space-y-5" data-testid="admin-customer-detail">
      <button data-testid="customer-detail-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to customers</button>

      <div className="ledger-card rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">{p.name || "Customer"}</h1>
            <p className="font-mono text-xs text-slate-500">{p.email} · {p.mobile || "no mobile"}</p>
            <p className="mt-1 font-mono text-[11px] text-slate-600">{p.user_id} · joined {p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : "—"}</p>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge status={suspended ? "BLOCKED" : "ACTIVE"} />
              <span className={`font-mono text-xs ${RISK_STYLE[d.risk_state]}`}>Risk: {d.risk_state}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {suspended
              ? <ActBtn testid="customer-reactivate" icon={CheckCircle2} label="Reactivate" onClick={() => act("REACTIVATE")} disabled={busy} className="border-emerald-500/40 text-emerald-300" />
              : <ActBtn testid="customer-suspend" icon={Ban} label="Suspend" onClick={() => act("SUSPEND", true)} disabled={busy} className="border-amber-500/40 text-amber-300" />}
            {d.risk_state === "NORMAL"
              ? <ActBtn testid="customer-flag" icon={ShieldAlert} label="Flag review" onClick={() => act("FLAG", true)} disabled={busy} className="border-indigo-500/40 text-indigo-300" />
              : <ActBtn testid="customer-unflag" icon={ShieldOff} label="Clear flag" onClick={() => act("UNFLAG", true)} disabled={busy} className="border-slate-600 text-slate-300" />}
            {d.risk_state !== "BLOCKED" && <ActBtn testid="customer-block" icon={Ban} label="Block" onClick={() => act("BLOCK", true)} disabled={busy} className="border-rose-500/40 text-rose-300" />}
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <Stat label="Available" value={d.wallet.available.display} accent />
          <Stat label="Pending" value={d.wallet.pending.display} />
          <Stat label="Total" value={d.wallet.total.display} />
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-800 pb-2">
        {DETAIL_TABS.map((t) => (
          <button key={t} data-testid={`customer-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium ${tab === t ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Tokens" value={d.tokens.length} />
          <Stat label="Cashback txns" value={d.cashback.length} />
          <Stat label="Redemptions" value={d.redemptions.length} />
          <Stat label="Ledger entries" value={d.wallet_ledger.length} />
        </div>
      )}
      {tab === "Wallet" && (
        <Mini cols={["Date", "Type", "Pending Δ", "Available Δ"]} testid="customer-wallet-ledger">
          {d.wallet_ledger.map((e) => (
            <tr key={e.entry_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-xs">{e.entry_type}</td>
              <td className={`p-3 font-mono ${e.pending_delta < 0 ? "text-rose-400" : "text-slate-300"}`}>₹{(e.pending_delta / 100).toLocaleString("en-IN")}</td>
              <td className={`p-3 font-mono ${e.available_delta < 0 ? "text-rose-400" : "text-emerald-400"}`}>₹{(e.available_delta / 100).toLocaleString("en-IN")}</td>
            </tr>
          ))}
        </Mini>
      )}
      {tab === "Tokens" && (
        <Mini cols={["Token", "Merchant", "State", "Cashback", "Date"]} testid="customer-tokens">
          {d.tokens.map((t) => (
            <tr key={t.tokenId} className="border-t border-slate-800/60">
              <td className="p-3 font-mono text-xs">{t.tokenId}</td>
              <td className="p-3 font-mono text-xs text-slate-500">{t.merchant_id}</td>
              <td className="p-3"><StatusBadge status={t.state === "USED" ? "USED" : "ACTIVE"} /></td>
              <td className="p-3 font-mono text-emerald-400">₹{((t.cashback_paise || 0) / 100).toLocaleString("en-IN")}</td>
              <td className="p-3 text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
            </tr>
          ))}
        </Mini>
      )}
      {tab === "Cashback" && (
        <Mini cols={["Date", "Merchant", "Amount", "Status"]} testid="customer-cashback">
          {d.cashback.map((c) => (
            <tr key={c.txn_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(c.created_at).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-xs text-slate-500">{c.merchant_id}</td>
              <td className="p-3 font-mono text-emerald-400">₹{(c.amount_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3"><StatusBadge status={c.status} /></td>
            </tr>
          ))}
        </Mini>
      )}
      {tab === "Redemptions" && (
        <Mini cols={["Date", "Amount", "Method", "Status"]} testid="customer-redemptions">
          {d.redemptions.map((r) => (
            <tr key={r.redemption_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
              <td className="p-3 font-mono">₹{(r.amount_paise / 100).toLocaleString("en-IN")}</td>
              <td className="p-3 text-slate-400">{r.method}</td>
              <td className="p-3"><StatusBadge status={r.status} /></td>
            </tr>
          ))}
        </Mini>
      )}
      {tab === "Fraud" && (
        <Mini cols={["Date", "State", "Reason"]} testid="customer-fraud">
          {d.fraud_flags.map((f) => (
            <tr key={f.flag_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(f.created_at).toLocaleString("en-IN")}</td>
              <td className={`p-3 font-mono text-xs ${RISK_STYLE[f.risk_state]}`}>{f.risk_state}</td>
              <td className="p-3 text-slate-300">{f.reason}</td>
            </tr>
          ))}
          {d.fraud_flags.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No fraud history.</td></tr>}
        </Mini>
      )}
      {tab === "Audit" && (
        <Mini cols={["Date", "Action", "Entity"]} testid="customer-audit">
          {d.audit.map((a) => (
            <tr key={a.audit_id} className="border-t border-slate-800/60">
              <td className="p-3 text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</td>
              <td className="p-3 font-mono text-xs text-emerald-400">{a.action}</td>
              <td className="p-3 text-xs text-slate-400">{a.entity}:{a.entity_id}</td>
            </tr>
          ))}
          {d.audit.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No audit events.</td></tr>}
        </Mini>
      )}
    </div>
  );
}

function ActBtn({ icon: Icon, label, onClick, disabled, className, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5 disabled:opacity-50 ${className}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 font-heading text-lg font-bold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p>
    </div>
  );
}

function Mini({ cols, children, testid }) {
  return (
    <div className="ledger-card overflow-hidden rounded-2xl">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-900/50 font-mono text-[11px] uppercase tracking-wider text-slate-500"><tr>{cols.map((c, i) => <th key={i} className="p-3 text-left">{c}</th>)}</tr></thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
